<script setup>
import { computed } from 'vue';

const props = defineProps({
  title: { type: String, required: true },
  icon: { type: String, required: true },
  nodeType: { type: String, required: true },
  kinds: { type: Array, required: true },
  isOpen: { type: Boolean, default: true },
  emptyLabel: { type: String, required: true },
  kindLabel: { type: Function, required: true },
  kindIcon: { type: Function, required: true },
  itemStyle: { type: Function, default: () => ({}) },
  itemIconStyle: { type: Function, default: () => ({}) },
  tone: { type: String, default: 'teal' },
});

const emit = defineEmits([
  'toggle',
  'add',
  'dragStart',
  'dragEnd',
  'hover',
  'leave',
  'press',
  'release',
]);

const toneClasses = computed(() =>
  props.tone === 'amber'
    ? {
        icon: 'bg-n-amber-9/10 text-n-amber-11',
        itemIcon: 'text-n-amber-11',
      }
    : {
        icon: 'bg-n-teal-9/10 text-n-teal-11',
        itemIcon: 'text-n-teal-11',
      }
);

const titleId = computed(
  () =>
    `${props.nodeType}-${props.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
);

const toggle = () => emit('toggle');
</script>

<template>
  <section class="space-y-1" :aria-labelledby="titleId">
    <button
      type="button"
      class="group flex w-full items-center gap-2 rounded-lg px-1.5 py-1.5 text-left outline-none transition-colors hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-teal-9"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <span
        class="flex size-6 shrink-0 items-center justify-center rounded-md"
        :class="toneClasses.icon"
        aria-hidden="true"
      >
        <span class="size-3.5 shrink-0" :class="icon" />
      </span>
      <span
        :id="titleId"
        class="min-w-0 flex-1 truncate text-xs font-semibold text-n-slate-12"
      >
        {{ title }}
      </span>
      <span class="text-[11px] tabular-nums text-n-slate-10">
        {{ kinds.length }}
      </span>
      <span
        class="size-4 shrink-0 text-n-slate-10 transition-transform duration-150"
        :class="isOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
        aria-hidden="true"
      />
    </button>

    <div v-if="isOpen" class="flex flex-col gap-1.5">
      <button
        v-for="kind in kinds"
        :key="kind"
        type="button"
        draggable="true"
        class="group flex w-full cursor-grab items-center gap-2.5 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2 text-left outline-none transition-[border-color,background-color,box-shadow,transform] hover:z-[1] hover:border-n-strong hover:bg-n-solid-2 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-n-teal-9 active:cursor-grabbing active:scale-[0.99]"
        :style="itemStyle(kind)"
        @dragstart.stop="emit('dragStart', $event, nodeType, kind)"
        @dragend="emit('dragEnd')"
        @mouseenter="emit('hover', kind)"
        @mouseleave="emit('leave')"
        @mousedown="emit('press', kind)"
        @mouseup="emit('release')"
        @click="emit('add', nodeType, kind)"
      >
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-md bg-n-solid-2 ring-1 ring-n-strong/15 transition-colors group-hover:bg-n-solid-3"
        >
          <span
            class="size-4 shrink-0"
            :class="[kindIcon(kind), toneClasses.itemIcon]"
            :style="itemIconStyle(kind)"
          />
        </span>
        <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
          {{ kindLabel(nodeType, kind) }}
        </span>
        <span
          class="size-4 shrink-0 text-n-slate-10 opacity-0 transition-opacity group-hover:opacity-100"
          :class="
            nodeType === 'trigger' ? 'i-lucide-plus' : 'i-lucide-grip-vertical'
          "
          aria-hidden="true"
        />
      </button>
    </div>

    <p
      v-if="isOpen && !kinds.length"
      class="rounded-lg border border-dashed border-n-weak px-3 py-3 text-center text-xs leading-snug text-n-slate-11"
    >
      {{ emptyLabel }}
    </p>
  </section>
</template>
